// Roam the city (docs/plans/2026-10-19-fixes-round-1.md, section C): the place picker over the district map. The
// title's card 03 opens it, and M opens it again while you roam. The places and where you stand come from
// src/shared/roam.ts; main.ts runs the roam itself (an offline sim with no clock, no police and no score).
import type { GameMap, MapJson } from '../shared/map';
import { RoamPlace, roamPlaces } from '../shared/roam';
import { MapArt } from './mapview';
import { FONT, INK } from './ui';

/** Where the district map and the landmark survey are served from. */
export const ROAM_MAP = '/maps/district.json', ROAM_PLACES = '/places/landmarks.csv';

/** The district's places, fetched once: the map file's metro stations and the landmark CSV. */
let placesQ: Promise<RoamPlace[]> | null = null;
export function loadPlaces(json: MapJson, fetchText: (url: string) => Promise<string>): Promise<RoamPlace[]> {
  placesQ ??= fetchText(ROAM_PLACES).catch(() => '').then((csv) => roamPlaces(json.pois ?? [], csv));
  return placesQ;
}

/**
 * The picker: the district map with a dot per place, and the places as a list (the list is the keyboard's way in).
 * A click on a dot or a name picks it; Esc or the close button puts it away.
 */
export class RoamPicker {
  open = false;
  private map: GameMap | null = null; private art: MapArt | null = null;
  private places: RoamPlace[] = [];
  private hot: RoamPlace | null = null;
  /** the place you are at now, ringed on the map */
  private here: RoamPlace | null = null;
  private onPick: (p: RoamPlace) => void = () => {};
  private onClose: () => void = () => {};
  private canvas: HTMLCanvasElement; private g: CanvasRenderingContext2D;
  private list: HTMLElement; private sub: HTMLElement;
  /** the map's fit on the canvas: css px per tile and the offset */
  private fit = { s: 1, ox: 0, oy: 0 };

  constructor(private root: HTMLElement) {
    root.innerHTML = `<div class="rp">
  <div class="rp-head"><h2 id="rp-h"><b>ROAM THE CITY</b><span>PICK A PLACE</span></h2><span class="rp-sub" data-sub></span><button type="button" data-close aria-label="Close" title="Close (Esc)">✕</button></div>
  <div class="rp-body"><canvas class="rp-map" aria-hidden="true"></canvas><div class="rp-list" data-list></div></div>
  <div class="rp-foot">Click a dot on the map or a name in the list · <kbd>Esc</kbd> closes</div>
</div>`;
    this.canvas = root.querySelector('canvas')!; this.g = this.canvas.getContext('2d')!;
    this.list = root.querySelector('[data-list]')!; this.sub = root.querySelector('[data-sub]')!;
    root.querySelector<HTMLElement>('[data-close]')!.onclick = () => this.close();
    root.addEventListener('click', (e) => { if (e.target === root) this.close(); }); // the backdrop
    this.canvas.addEventListener('pointermove', (e) => { this.setHot(this.nearest(e)); });
    this.canvas.addEventListener('pointerleave', () => this.setHot(null));
    this.canvas.addEventListener('click', (e) => { const p = this.nearest(e); if (p) this.pick(p); });
    addEventListener('resize', () => { if (this.open) this.draw(); });
  }

  /** The map and its places; the list is rebuilt, the painting is made on the first draw. */
  setMap(map: GameMap, places: RoamPlace[]) {
    if (this.map !== map) { this.map = map; this.art = new MapArt(map, { roadScale: 0.7 }); }
    if (this.places === places) return;
    this.places = places; this.list.textContent = '';
    let group = '';
    for (const p of places) {
      if (p.group !== group) { group = p.group; const h = document.createElement('h3'); h.textContent = group; this.list.append(h); }
      const b = document.createElement('button');
      b.type = 'button'; b.dataset.id = p.id; b.className = p.kind; b.textContent = p.name;
      b.onclick = () => this.pick(p);
      b.onmouseenter = b.onfocus = () => this.setHot(p);
      this.list.append(b);
    }
  }

  /** `here`: the place you roam from now (null from the title screen). */
  show(o: { here: RoamPlace | null; onPick: (p: RoamPlace) => void; onClose: () => void }) {
    this.here = o.here; this.onPick = o.onPick; this.onClose = o.onClose; this.hot = null;
    this.sub.textContent = o.here ? `You are at ${o.here.name}. Pick somewhere else` : 'Offline · no clock, no police, no score · start anywhere';
    this.open = true; this.root.hidden = false;
    this.draw();
    const first = (o.here && this.list.querySelector<HTMLElement>(`button[data-id="${CSS.escape(o.here.id)}"]`)) || this.list.querySelector<HTMLElement>('button');
    first?.focus({ preventScroll: false });
  }
  hide() { this.open = false; this.root.hidden = true; }
  close() { if (!this.open) return; this.hide(); this.onClose(); }
  private pick(p: RoamPlace) { this.hide(); this.onPick(p); }

  private setHot(p: RoamPlace | null) { if (p !== this.hot) { this.hot = p; this.draw(); } }
  /** The place under the pointer, within 14 css px. */
  private nearest(e: { clientX: number; clientY: number }): RoamPlace | null {
    const r = this.canvas.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
    let best: RoamPlace | null = null, bd = 14;
    for (const p of this.places) {
      const d = Math.hypot(this.fit.ox + p.x * this.fit.s - x, this.fit.oy + p.y * this.fit.s - y);
      if (d < bd) { bd = d; best = p; }
    }
    return best;
  }

  /** The map fitted to the canvas, the landmarks as ink dots, the stations as metro squares, the hot one named. */
  draw() {
    const m = this.map, c = this.canvas, g = this.g;
    if (!m || !this.art || !this.open) return;
    const dpr = Math.min(2, devicePixelRatio || 1), W = c.clientWidth, H = c.clientHeight;
    if (!W || !H) return;
    if (c.width !== Math.round(W * dpr) || c.height !== Math.round(H * dpr)) { c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); }
    const s = Math.min(W / m.w, H / m.h), ox = (W - m.w * s) / 2, oy = (H - m.h * s) / 2;
    this.fit = { s, ox, oy };
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = INK.newsprint2; g.fillRect(0, 0, W, H);
    const art = this.art.at(s * dpr);
    g.imageSmoothingEnabled = true; g.drawImage(art, ox, oy, m.w * s, m.h * s);
    g.lineWidth = 2; g.strokeStyle = INK.asphalt; g.strokeRect(ox, oy, m.w * s, m.h * s);
    const at = (p: RoamPlace) => ({ x: ox + p.x * s, y: oy + p.y * s });
    for (const p of this.places) {
      if (p.kind !== 'landmark') continue;
      const q = at(p); g.beginPath(); g.arc(q.x, q.y, 3.5, 0, Math.PI * 2);
      g.fillStyle = INK.asphalt; g.fill(); g.lineWidth = 1.5; g.strokeStyle = INK.newsprint; g.stroke();
    }
    for (const p of this.places) {
      if (p.kind !== 'metro') continue;
      const q = at(p); g.fillStyle = INK.metro; g.fillRect(q.x - 6, q.y - 6, 12, 12);
      g.lineWidth = 2; g.strokeStyle = INK.asphalt; g.strokeRect(q.x - 6, q.y - 6, 12, 12);
    }
    if (this.here) { const q = at(this.here); g.lineWidth = 3; g.strokeStyle = INK.riso; g.beginPath(); g.arc(q.x, q.y, 11, 0, Math.PI * 2); g.stroke(); }
    const h = this.hot;
    if (h) {
      const q = at(h);
      g.lineWidth = 3; g.strokeStyle = INK.asphalt; g.beginPath(); g.arc(q.x, q.y, 9, 0, Math.PI * 2); g.stroke();
      g.font = FONT.body(13, 700); const tw = g.measureText(h.name).width + 14;
      const tx = Math.max(4, Math.min(W - tw - 4, q.x - tw / 2)), ty = q.y - 36 < 4 ? q.y + 14 : q.y - 36;
      g.fillStyle = INK.asphalt; g.fillRect(tx + 3, ty + 3, tw, 24);
      g.fillStyle = h.kind === 'metro' ? INK.metro : INK.newsprint; g.fillRect(tx, ty, tw, 24);
      g.lineWidth = 2; g.strokeStyle = INK.asphalt; g.strokeRect(tx, ty, tw, 24);
      g.fillStyle = INK.asphalt; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(h.name, tx + 7, ty + 12.5);
      g.textBaseline = 'alphabetic';
    }
    this.list.querySelectorAll<HTMLElement>('button').forEach((b) => b.classList.toggle('hot', b.dataset.id === h?.id));
  }
}
